import { Option, OptionTypes } from "@/types/productTypes";
import { produce } from "immer";

import fetchDb from "../utils/customRequestFunction";
import { getOption } from "../utils/getFunction";

import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";

export interface OptionStoreState {
   selectedOptionId: number | null;
   allOptions: Option[];
   fetchAllOptions: (productId: number) => Promise<void>;
   addOption: (productId: number, stageId: number) => Promise<void>;
   updateOption: (
      productId: number,
      stageId: number,
      optionId: number,
      field: keyof Option,
      value: any
   ) => Promise<void>;
   deleteOption: (
      productId: number,
      stageId: number,
      optionId: number
   ) => Promise<void>;
   reorderOptions: (
      id1: number,
      id2: number,
      stageId: number,
      productId: number
   ) => Promise<void>;
   setSelectedOptionId: (id: number | null) => void;
   selectOption: (stageId: number, optionId: number) => void;
}
export const createOptionSlice: AdminSliceCreator<OptionStoreState> = (
   set,
   get
) => ({
   selectedOptionId: null,
   allOptions: [],

   fetchAllOptions: async (productId) => {
      try {
         const data = await fetchDb.get(`/api/options/product/${productId}`);
         set(
            produce((state: AdminStoreState) => {
               state.allOptions = data;
            })
         );
      } catch (error) {
         console.error("Error fetching options:", error);
      }
   },

   addOption: async (productId, stageId) => {
      try {
         const newOptionData = {
            name: "New Option",
            optionId: `option-${Date.now()}`,
            type: OptionTypes.RADIO,
            relativePos:
               get()
                  .allProducts?.find((p) => p.id === productId)
                  ?.stages.find((s) => s.id === stageId)?.options?.length || 0,
            stageId: stageId,
            defaultValue: -1,
         };

         const createdOption: Option = await fetchDb.post(
            "/api/options",
            newOptionData
         );

         set(
            produce((state: AdminStoreState) => {
               const product = state.allProducts?.find(
                  (p) => p.id === productId
               );
               if (product) {
                  const stage = product.stages.find((s) => s.id === stageId);
                  if (stage) {
                     if (!stage.options) stage.options = [];
                     stage.options.push(createdOption);
                  }
               }
            })
         );
         if (createdOption.id) {
            get().selectOption(stageId, createdOption.id);
         }
      } catch (error) {
         console.error("Error adding option:", error);
      }
   },

   updateOption: async (productId, stageId, optionId, field, value) => {
      set(
         produce((state: AdminStoreState) => {
            const allProducts = state.allProducts;
            if (!allProducts) return;
            const option = getOption(allProducts, productId, stageId, optionId);
            if (option) {
               (option as any)[field] = value;
            }
         })
      );

      try {
         await fetchDb.patch(`/api/options/${optionId}`, { [field]: value });
      } catch (error) {
         console.error("Error updating option:", error);
      }
   },

   deleteOption: async (productId, stageId, optionId) => {
      set(
         produce((state: AdminStoreState) => {
            const product = state.allProducts?.find((p) => p.id === productId);
            if (product) {
               const stage = product.stages.find((s) => s.id === stageId);
               if (stage) {
                  stage.options = stage.options?.filter(
                     (o) => o.id !== optionId
                  );
               }
            }
         })
      );

      try {
         await fetchDb.delete(`/api/options/${optionId}`);
      } catch (error) {
         console.error("Error deleting option:", error);
      }
   },

   reorderOptions: async (id1, id2, stageId, productId) => {
      set(
         produce((state: AdminStoreState) => {
            const product = state.allProducts?.find((p) => p.id === productId);
            if (product) {
               const stage = product.stages.find((s) => s.id === stageId);
               if (stage && stage.options) {
                  const option1 = stage.options.find((o) => o.id === id1);
                  const option2 = stage.options.find((o) => o.id === id2);
                  if (option1 && option2) {
                     [option1.relativePos, option2.relativePos] = [
                        option2.relativePos,
                        option1.relativePos,
                     ];
                     stage.options.sort(
                        (a, b) => a.relativePos - b.relativePos
                     );
                  }
               }
            }
         })
      );

      try {
         await fetchDb.patch(`/api/options/reorder/${id1}/${id2}`);
      } catch (error) {
         console.error("Error reordering options:", error);
      }
   },

   setSelectedOptionId: (id: number | null) => {
      set(
         produce((state: AdminStoreState) => {
            state.selectedOptionId = id;
         })
      );
   },

   selectOption: (stageId, optionId) => {
      set(
         produce((state: AdminStoreState) => {
            state.selectedStageId = stageId;
            state.selectedOptionId = optionId;
            state.selectedItemId = null;
         })
      );
   },
});
